import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Stack,
  useBoolean,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { HiOutlineFilter } from "react-icons/hi";
import { useLocation } from "react-router-dom";

import { PROPERTY_TYPES } from "src/common/types";
import { useGetAllDealerOrganizationsQuery } from "src/routes/Properties/queries";

type FilterFormValues = {
  dealerOrganizationId: string;
  propertyType: string[];
};

interface Props {
  onSubmit(searchParams: URLSearchParams): void;
}

export const PropertiesFilterPopover = ({ onSubmit }: Props) => {
  const [isEditing, setIsEditing] = useBoolean();
  const location = useLocation();
  const sp = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { control, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: {
      dealerOrganizationId: sp.get("dealerOrganizationId") || "",
      propertyType: sp.get("propertyType")?.split(",") || [],
    },
  });

  const parseParameters = useCallback(
    (data: FilterFormValues) => {
      const newSp = new URLSearchParams(location.search);

      for (const [key, value] of Object.entries(data)) {
        if (value?.length) {
          newSp.set(key, value as string);
        } else {
          newSp.delete(key);
        }
      }

      onSubmit(newSp);
      setIsEditing.off();
    },
    [location.search]
  );

  const dealerOrganizationsQuery = useGetAllDealerOrganizationsQuery();

  return (
    <Popover
      isOpen={isEditing}
      onOpen={setIsEditing.on}
      onClose={setIsEditing.off}
      closeOnBlur={false}
      isLazy
      lazyBehavior="keepMounted"
    >
      <PopoverTrigger>
        <Button leftIcon={<HiOutlineFilter />}>Filters</Button>
      </PopoverTrigger>

      <PopoverContent display="flex">
        <PopoverHeader>Filters</PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody>
          <form onSubmit={handleSubmit(parseParameters)}>
            <VStack align="start" gap="6">
              <Box width="100%">
                <Controller
                  name="dealerOrganizationId"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="dealerOrganizationId">
                        Dealer
                      </FormLabel>
                      <Select
                        {...field}
                        id="dealerOrganizationId"
                        isDisabled={dealerOrganizationsQuery.isLoading}
                      >
                        <option value=""></option>
                        {dealerOrganizationsQuery.data?.map((dealerOrg) => (
                          <option
                            value={dealerOrg.dealerOrganizationId}
                            key={dealerOrg.dba || dealerOrg.name}
                          >
                            {dealerOrg.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <Controller
                  name="propertyType"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="propertyType">
                        Property Type
                      </FormLabel>
                      <CheckboxGroup {...field} isDisabled={field.disabled}>
                        <Stack
                          spacing={[1, 5]}
                          direction={["column", "row"]}
                          id="propertyType"
                        >
                          {" "}
                          {PROPERTY_TYPES.map((type) => (
                            <Checkbox key={type} value={type}>
                              {type}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <ButtonGroup display="flex" justifyContent="flex-end">
                  <Button type="button" onClick={setIsEditing.off}>
                    Cancel
                  </Button>
                  <Button type="submit" colorScheme="brand.blue">
                    Apply Filters
                  </Button>
                </ButtonGroup>
              </Box>
            </VStack>
          </form>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
